import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const BookingDetails = () => {
  const { bookid } = useParams();
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [showActions, setShowActions] = useState(false);

const [isSendingInvite, setIsSendingInvite] = useState(false);
const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingResponse, reviewResultResponse] = await Promise.all([
          axios.get(`${config.apiBaseUrl}/api/book/bookings/${bookid}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
          })
        ]);
        
        setBookingDetails(bookingResponse.data);
        setEditedDetails(bookingResponse.data);
        setReviewResult(reviewResultResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [bookid]);

  const handleEdit = () => setIsEditing(true);

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${config.apiBaseUrl}/api/book/bookings/${bookid}`, 
        {
          customer_name: editedDetails.customer_name,
          phone: editedDetails.phone,
          email: editedDetails.email,
          visatype: editedDetails.visatype,
          plan: editedDetails.plan,
          drive: editedDetails.drive,
          nation: editedDetails.nation,
          occ: editedDetails.occ, 
          anything: editedDetails.anything,
          status: editedDetails.status,
          razid: editedDetails.razid,
          amount: editedDetails.amount,
          // Using the exact field names that match the backend columns
          quantity: editedDetails.quantity,
          application_country: editedDetails.application_country,
          travelers_count: editedDetails.travelers_count,
          visa_refused: editedDetails.visa_refused
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
   
      if (response.data.success) {
        window.location.reload();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating booking details');
    }
  };

  const handleSendInvite = async () => {
    if (!window.confirm('Send Google Meet invite for this appointment?')) return;
    setIsSendingInvite(true);
    try {
      await axios.post(
        `${config.apiBaseUrl}/api/book/bookings/${bookid}/send-invite`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      alert('Calendar invite sent successfully!');
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send invite');
    } finally {
      setIsSendingInvite(false);
    }
  };
  
  const handleMarkCompleted = async () => {
    if (!window.confirm('Mark this appointment as Completed?')) return;
    setIsMarkingCompleted(true);
    try {
      await axios.put(
        `${config.apiBaseUrl}/api/book/bookings/${bookid}/complete-appointment`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      alert('Appointment marked as completed!');
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to mark as completed');
    } finally {
      setIsMarkingCompleted(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await axios.delete(`${config.apiBaseUrl}/api/book/deletebooking/${bookid}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        });
        navigate('/bookings');
      } catch (error) {
        console.error('Error deleting booking:', error);
      }
    }
  };

  const handleInputChange = (e) => {
    setEditedDetails({
      ...editedDetails,
      [e.target.name]: e.target.value
    });
  };

  const sendFeedbackEmail = async (bookId, orderId, serviceName, toEmail, customerName) => {
    try {
      // Construct the request body with the parameters
      const requestBody = {
        orderId,
        bookId,
        serviceName,
        toEmail,
        customerName
      };
  
      // Make the POST request with axios
      const response = await axios.post(
        `${config.apiBaseUrl}/api/book/sendfeedback/${bookId}`,
        requestBody,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        }
      );
  
      // Handle success response
      if (response.status === 200) {
        alert('Feedback email sent successfully!');
      } else {
        alert(`Failed to send feedback email: ${response.statusText}`);
      }
    } catch (error) {
      // Handle errors
      console.error('Error sending feedback email:', error);
      alert(`Error sending feedback email: ${error.response?.data?.error || error.message}`);
    }
  };
  
  const renderField = (label, field) => {
    const selectOptions = {
      status: ['Payment Due', 'processing', 'Cancelled', 'Review', 'Completed'],
      visatype: ['Schengen Visa', 'Ireland Visa', 'Canada Visa', 'United Kingdom Visa', 'Japan Visa', 'Australia Visa'],
      occ: ['Salaried', 'Self-Employed', 'Student', 'Retired', 'Unemployed'],
      plan: ['Get Reviewed', 'A-Z prep'],
      nation: ['India','Other'],
      // Added new select option for visa_refused
      visa_refused: ['Yes', 'No']
    };

    return (
      <div className="mb-4">
        <strong className="block text-sm text-gray-400 uppercase mb-1">{label}</strong>
        {isEditing ? (
          selectOptions[field] ? (
            <select
              name={field}
              value={editedDetails[field] || ''}
              onChange={handleInputChange}
              className="w-full p-2 lg:p-3 rounded text-black text-sm lg:text-base"
            >
              <option value="">Select {label}</option>
              {selectOptions[field].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <textarea
              name={field}
              value={editedDetails[field] || ''}
              onChange={handleInputChange}
              rows={1} // Default size
              className="w-full p-2 lg:p-3 rounded text-black text-sm lg:text-base resize-y"
            />
          )
        ) : (
          <div className="text-white text-sm lg:text-base break-words">
            {bookingDetails[field] || 'Not specified'}
          </div>
        )}
      </div>
    );
  };

  if (!bookingDetails) return (
    <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
      <div className="text-lg">Loading...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-800 text-white">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gray-900 p-4 flex justify-between items-center">
        <button 
          onClick={() => navigate('/bookings')} 
          className="text-white hover:text-gray-300"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth={1.5} 
            stroke="currentColor" 
            className="w-6 h-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h1 className="text-lg font-bold">Booking Details</h1>
        <button 
          onClick={() => setShowActions(!showActions)}
          className="text-white hover:text-gray-300"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth={1.5} 
            stroke="currentColor" 
            className="w-6 h-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
          </svg>
        </button>
      </div>

      {/* Mobile Actions Panel */}
      {showActions && (
        <div className="lg:hidden bg-gray-700 p-4 border-b border-gray-600">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold">Actions</h2>
            <button 
              onClick={() => setShowActions(false)}
              className="text-gray-300 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="space-y-2">
            {bookingDetails.status === 'Review' && (
              <button 
                onClick={() => {
                  sendFeedbackEmail(
                    bookingDetails.bookid,         
                    bookingDetails.bid,        
                    bookingDetails.visatype + ' - ' + bookingDetails.plan,    
                    bookingDetails.customer_email,        
                    bookingDetails.customer_name    
                  );
                  setShowActions(false);
                }} 
                className="w-full bg-purple-500 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded transition-colors"
              >
                Send Feedback Email
              </button>
            )}
            <button 
              onClick={() => {
                isEditing ? handleSave() : handleEdit();
                if (isEditing) setShowActions(false);
              }} 
              className="w-full bg-green-500 hover:bg-green-700 text-white font-bold py-3 px-4 rounded transition-colors"
            >
              {isEditing ? 'Save Changes' : 'Edit Booking'}
            </button>
            <button 
              onClick={() => {
                handleDelete();
                setShowActions(false);
              }} 
              className="w-full bg-red-500 hover:bg-red-700 text-white font-bold py-3 px-4 rounded transition-colors"
            >
              Delete Booking
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 lg:p-6">
        {/* Desktop Header */}
        <div className="hidden lg:flex justify-between items-center mb-6">
          <button 
            onClick={() => navigate('/bookings')} 
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
          >
            Back
          </button>
          <div className="flex gap-2">
            {bookingDetails.status === 'Review' && (
              <button 
                onClick={() => sendFeedbackEmail(
                  bookingDetails.bookid,         
                  bookingDetails.bid,        
                  bookingDetails.visatype + ' - ' + bookingDetails.plan,    
                  bookingDetails.customer_email,        
                  bookingDetails.customer_name    
                )} 
                className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded transition-colors"
              >
                Send Feedback Email
              </button>
            )}
            <button 
              onClick={isEditing ? handleSave : handleEdit} 
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              {isEditing ? 'Save' : 'Edit'}
            </button>
            <button 
              onClick={handleDelete} 
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Review Results */}
        {reviewResult && (
          <div className="mb-6 p-4 bg-gray-700 rounded-lg">
            <h2 className="text-lg lg:text-xl font-bold mb-3">Review Results</h2>
            <div className="space-y-2">
              <p className="text-sm lg:text-base"><strong>Rating:</strong> {reviewResult.rating}</p>
              <p className="text-sm lg:text-base"><strong>Comment:</strong> {reviewResult.comment}</p>
            </div>
          </div>
        )}

        {/* Booking Details Title */}
        <h1 className="hidden lg:block text-3xl font-bold mb-6">Booking Details</h1>

        {/* Booking Details Content */}
        <div className="bg-gray-700 p-4 lg:p-6 rounded-lg shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left Column */}
            <div className="space-y-1">
              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Order ID</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {bookingDetails.bid}
                </div>
              </div>

              {renderField('Customer Name', 'customer_name')}

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Account Name</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {bookingDetails.account_name}
                </div>
              </div>

              {renderField('Booking Email', 'email')}

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Account Email</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {bookingDetails.customer_email}
                </div>
              </div>

              {renderField('Customer Phone', 'phone')}
              {renderField('Visa Type', 'visatype')}
              {renderField('Plan', 'plan')}

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Booking ID</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {bookingDetails.bookid}
                </div>
              </div>

              {renderField('Payment ID', 'razid')}
              {renderField('Amount', 'amount')}
              {renderField('Quantity', 'quantity')}
              {renderField('Number of Travellers', 'travelers_count')}
            </div>

            {/* Right Column */}
            <div className="space-y-1">
              {renderField('Drive', 'drive')}
              {renderField('Nation', 'nation')}
              {renderField('Occupation', 'occ')}
              {renderField('Country of Application', 'application_country')}
              {renderField('Visa Refused', 'visa_refused')}
              {renderField('Additional Info', 'anything')}

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Promo</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {bookingDetails.promo}
                </div>
              </div>

              {renderField('Status', 'status')}

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Appointments</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {bookingDetails.appointment_count}
                </div>
              </div>

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Created On</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {new Date(bookingDetails.createdon).toLocaleString()}
                </div>
              </div>

              <div className="mb-4">
                <strong className="block text-sm text-gray-400 uppercase mb-1">Updated On</strong>
                <div className="text-white text-sm lg:text-base break-words">
                  {new Date(bookingDetails.updatedon).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

{/* Appointment Section */}
{bookingDetails.appid && (() => {
  const aptDatetime = new Date(bookingDetails.appointment_datetime);
  const aptDateIST = aptDatetime.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short'
  });

  const canSendInvite =
    bookingDetails.status === 'processing' &&
    bookingDetails.payment_status === 'Success' &&
    bookingDetails.appointment_link === 'None';

  const inviteAlreadySent = bookingDetails.appointment_status === 'Confirmed'
    || bookingDetails.appointment_status === 'Completed';

  const canMarkCompleted =
    bookingDetails.appointment_status === 'Confirmed' &&
    aptDatetime < new Date();

  return (
    <div className="mt-6 bg-gray-700 p-4 lg:p-6 rounded-lg shadow-lg">
      <h2 className="text-lg lg:text-xl font-bold mb-4 text-white">Appointment</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div>
          <strong className="block text-sm text-gray-400 uppercase mb-1">Call Type</strong>
          <div className="text-white text-sm lg:text-base">
            {bookingDetails.appointment_app_name || 'N/A'}
          </div>
        </div>
        <div>
          <strong className="block text-sm text-gray-400 uppercase mb-1">Scheduled Date & Time (IST)</strong>
          <div className="text-white text-sm lg:text-base font-semibold">
            {aptDateIST}
          </div>
        </div>
        <div>
          <strong className="block text-sm text-gray-400 uppercase mb-1">Appointment Status</strong>
          <div className={`text-sm lg:text-base font-semibold ${
            bookingDetails.appointment_status === 'Confirmed' ? 'text-green-400' :
            bookingDetails.appointment_status === 'Completed' ? 'text-blue-400' :
            'text-yellow-400'
          }`}>
            {bookingDetails.appointment_status}
          </div>
        </div>
        <div>
          <strong className="block text-sm text-gray-400 uppercase mb-1">Meet Link</strong>
          <div className="text-sm lg:text-base">
          {bookingDetails.appointment_link && bookingDetails.appointment_link !== 'None' ? (
  <a href={bookingDetails.appointment_link}
  target="_blank"
  rel="noreferrer"
  className="text-blue-400 underline break-all"
>
  {bookingDetails.appointment_link}
</a>
            ) : (
              <span className="text-gray-400">Not generated yet</span>
            )}
          </div>
        </div>
      </div>

      {/* Invite Preview — shown only when invite can be sent */}
      {canSendInvite && (
        <div className="mb-4 p-3 bg-gray-600 rounded-lg border border-gray-500">
          <p className="text-xs text-gray-400 uppercase mb-2 font-semibold">Invite Preview</p>
          <p className="text-white text-sm">
            <strong>Title:</strong>{' '}
            {bookingDetails.appointment_app_name} -{' '}
            {bookingDetails.customer_name?.trim().split(' ')[0]}
            &lt;&gt;Priyesh(Viszapp)
          </p>
          <p className="text-white text-sm mt-1">
            <strong>When:</strong> {aptDateIST} · 60 mins
          </p>
          <p className="text-white text-sm mt-1">
            <strong>Guest(s):</strong>{' '}
            {[...new Set([bookingDetails.appointment_email, bookingDetails.customer_email].filter(Boolean))].join(', ')}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        {(canSendInvite || inviteAlreadySent) && (
          <button
            onClick={canSendInvite ? handleSendInvite : undefined}
            disabled={!canSendInvite || isSendingInvite}
            className={`font-bold py-2 px-4 rounded transition-colors text-white text-sm ${
              inviteAlreadySent
                ? 'bg-gray-500 cursor-not-allowed opacity-60'
                : 'bg-blue-500 hover:bg-blue-700'
            }`}
          >
            {isSendingInvite
              ? 'Sending...'
              : inviteAlreadySent
              ? '✓ Invite Sent'
              : 'Send Invite'}
          </button>
        )}

        {canMarkCompleted && (
          <button
            onClick={handleMarkCompleted}
            disabled={isMarkingCompleted}
            className="bg-green-500 hover:bg-green-700 disabled:opacity-60 text-white font-bold py-2 px-4 rounded transition-colors text-sm"
          >
            {isMarkingCompleted ? 'Updating...' : 'Mark Completed'}
          </button>
        )}
      </div>
    </div>
  );
})()}
        {/* Mobile Edit Mode Actions */}
        {isEditing && (
          <div className="lg:hidden mt-6 flex gap-2">
            <button 
              onClick={handleSave} 
              className="flex-1 bg-green-500 hover:bg-green-700 text-white font-bold py-3 px-4 rounded transition-colors"
            >
              Save Changes
            </button>
            <button 
              onClick={() => setIsEditing(false)} 
              className="flex-1 bg-gray-500 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetails;